import { envConfig } from '#constants/envConfig';
import { localStorage } from '#utils/localStorage';

/**
 * First-touch attribution: remembers where a visitor came from, once, on their first page.
 *
 * `getUtmData()` has always read these `first_*` keys and sent them with every signup, but nothing
 * in the app ever wrote them — the values existed only if a Google Tag Manager tag happened to fill
 * them in, and that tag is gated behind analytics consent. Everyone who declined consent, blocked
 * GTM or arrived before it loaded signed up with an empty source, which is why almost every
 * registration reads as "direct".
 *
 * Written here instead: no consent gate, no third-party script, no network. Only the keys already
 * consumed by `getUtmData()` are touched, so both signup paths (email and OAuth) pick this up with
 * no further changes.
 */

const KEYS = {
  campaign: 'first_campaign',
  landingPage: 'first_landing_page',
  medium: 'first_medium',
  referrer: 'first_referrer',
  source: 'first_source',
} as const;

/** Referrer hosts that mean "the user was already on our site" — never an acquisition source. */
const isInternalHost = (host: string, currentHost: string) =>
  host === currentHost || host === 'teamplay.gg' || host.endsWith('.teamplay.gg');

const SEARCH_ENGINES = [
  'google.',
  'bing.',
  'yandex.',
  'duckduckgo.',
  'yahoo.',
  'ecosia.',
  'baidu.',
  'search.brave.',
];

/**
 * Assistants are worth their own medium: they already send real traffic (chatgpt.com shows up in
 * the report), and lumping them into "referral" hides the one channel we cannot buy.
 */
const AI_ASSISTANTS = ['chatgpt.com', 'chat.openai.com', 'perplexity.ai', 'claude.ai', 'copilot.'];

const classifyReferrer = (host: string) => {
  if (SEARCH_ENGINES.some((engine) => host.includes(engine))) {
    return 'organic';
  }

  if (AI_ASSISTANTS.some((assistant) => host.includes(assistant))) {
    return 'ai';
  }

  return 'referral';
};

/**
 * Приглашение друга — свой источник, а не «прямой заход».
 *
 * Ссылка приходит в чужой чат без реферера, поэтому человек, приведённый другом, до сих пор
 * выглядел в отчёте безымянным `direct`: самый ценный тип регистрации был неотличим от случайного.
 *
 * Сам код никуда не сохраняется — ни в кампанию, ни в landing page. Две причины. Кампания
 * группируется в отчёте (`source, medium, campaign`), и уникальный код на каждую регистрацию
 * разнёс бы таблицу кампаний в пыль из строк по единице — чем лучше работают приглашения, тем
 * хуже становился бы отчёт. Плюс код открывает место в сессии, и хранить его рядом с аккаунтом
 * незачем: кто кого позвал, знает журнал приглашений в teamplay-post, у него на это есть и
 * ссылка, и оба идентификатора.
 */
const INVITE_PARAM = 'invite';

type FirstTouch = {
  campaign: string | null;
  landingPage: string;
  medium: string;
  referrer: string;
  source: string;
};

/**
 * Landing page keeps the path and the utm_* parameters only.
 *
 * The raw query string is not safe to store: people land on confirmation and invite links whose
 * parameters are one-time secrets, and this value is persisted server-side next to the account.
 */
const landingPageOf = (params: URLSearchParams) => {
  const kept = new URLSearchParams();
  for (const [key, value] of params) {
    if (key.startsWith('utm_')) {
      kept.append(key, value);
    }
  }

  const query = kept.toString();
  return query ? `${window.location.pathname}?${query}` : window.location.pathname;
};

/**
 * Which store build we are running in. The web bundle is built once and copied into both the
 * Android and the iOS project, so `IS_NATIVE` cannot tell them apart — the user agent can.
 */
const nativeSource = () => {
  const ua = navigator.userAgent;

  if (ua.includes('Android')) {
    return 'android-app';
  }

  if (/iPhone|iPad|iPod|Macintosh/.test(ua)) {
    return 'ios-app';
  }

  return 'app';
};

const resolve = (): FirstTouch => {
  const params = new URLSearchParams(window.location.search);
  const referrer = document.referrer || '';
  const landingPage = landingPageOf(params);

  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const campaign = params.get('utm_campaign');

  // Inside the store build the app itself is the source, and it wins over everything below:
  // the Capacitor shell runs from the `https://localhost` origin with no referrer at all, so
  // every signup from the app would otherwise be recorded as "direct" and the app would be
  // invisible in the acquisition report. A campaign tag on a deep link is still kept — it says
  // which push or mail opened the app, which is a different question from which platform it is.
  if (envConfig.IS_NATIVE) {
    return {
      campaign,
      landingPage,
      medium: 'app',
      referrer,
      source: nativeSource(),
    };
  }

  // Приглашение сильнее utm-меток: метка говорит, из какой рассылки ссылка, а приглашение — что
  // за человеком стоит конкретный друг. Второе объясняет регистрацию точнее.
  const inviteCode = params.get(INVITE_PARAM);

  if (inviteCode) {
    return {
      campaign,
      landingPage,
      medium: 'invite',
      referrer,
      source: 'invite',
    };
  }

  // A tagged link is the strongest signal we have: it says what the source thinks it is.
  if (utmSource) {
    return {
      campaign,
      landingPage,
      medium: utmMedium || 'referral',
      referrer,
      source: utmSource,
    };
  }

  // The old TWA wrapper is a browser tab in disguise: it opens the site with an `android-app://`
  // referrer — our own app, not somebody else's website, and it used to land in the report as a
  // plain referral. Kept for everyone still on that build until the Capacitor update reaches them.
  if (referrer.startsWith('android-app://')) {
    return {
      campaign,
      landingPage,
      medium: 'app',
      referrer,
      source: 'android-app',
    };
  }

  if (referrer) {
    try {
      const host = new URL(referrer).hostname.replace(/^www\./, '');

      if (!isInternalHost(host, window.location.hostname.replace(/^www\./, ''))) {
        return {
          campaign,
          landingPage,
          medium: classifyReferrer(host),
          referrer,
          source: host,
        };
      }
    } catch {
      // A referrer we cannot parse tells us nothing — fall through to direct.
    }
  }

  return {
    campaign,
    landingPage,
    medium: 'none',
    referrer,
    source: 'direct',
  };
};

/**
 * Records the first touch. Safe to call on every load: once a source is stored it is never
 * overwritten, so a visitor who arrives from a search and signs up a week later still counts as
 * search, not as the direct visit that happened in between.
 */
export const captureFirstTouch = () => {
  // Everything is inside the try on purpose: this runs before hydration, and merely *touching*
  // window.localStorage throws in some privacy modes. An uncaught throw here would take the whole
  // app down before it renders.
  try {
    if (!window.localStorage || localStorage.getItem(KEYS.source)) {
      return;
    }

    const touch = resolve();

    localStorage.setItem(KEYS.source, touch.source);
    localStorage.setItem(KEYS.medium, touch.medium);
    localStorage.setItem(KEYS.landingPage, touch.landingPage);
    localStorage.setItem(KEYS.referrer, touch.referrer);

    if (touch.campaign) {
      localStorage.setItem(KEYS.campaign, touch.campaign);
    }
  } catch {
    // Private mode or a full quota — attribution is not worth breaking the page over.
  }
};
